import React from "react";
import { Calculator, Receipt } from "lucide-react";
import { Hero, Section, Sub, Callout, Bold } from "./_shared";
import { useT } from "@/i18n";

export default function TaxationPage() {
  const { lang } = useT();
  return lang === "en" ? <TaxationEN /> : <TaxationFR />;
}

// =============================================================
// FR
// =============================================================
function TaxationFR() {
  return (
    <div data-testid="view-taxation" className="max-w-5xl">
      <Hero
        moduleNum="05"
        eyebrowLabel="Module"
        icon={Calculator}
        italic="Fiscalité"
        accent="& rémunération"
        description="C'est le module que la plupart des intermédiaires négligent jusqu'au jour où ils reçoivent un redressement fiscal ou réalisent qu'ils ont laissé passer des années de revenus mal optimisés. Structurer correctement sa rémunération est aussi important que de conclure les deals eux-mêmes."
      />

      <Section eyebrow="Modes" title="Comment se rémunérer légalement" index="5.1">
        <Sub title="Mode 1 : La Commission de Courtage (Brokerage Fee / Success Fee)">
          <p>
            C'est le mode le plus courant. La commission n'est payable QUE si la transaction est effectivement finalisée
            (paiement reçu). Elle doit être formellement stipulée dans l'IMFPA avec : le pourcentage exact, la base de
            calcul (sur quel montant), la devise de paiement, le compte bancaire bénéficiaire, et le délai de paiement
            (souvent simultané au règlement principal, via la banque correspondante).
          </p>
          <p>
            Fiscalement, c'est un revenu d'activité professionnelle. Il doit être déclaré dans le pays de résidence fiscale
            du bénéficiaire et potentiellement dans le pays de source selon les conventions applicables.
          </p>
        </Sub>

        <Sub title="Mode 2 : Les Honoraires de Conseil (Consulting / Advisory Fee)">
          <p>
            Indépendant de la réussite du deal. Vous êtes rémunéré pour votre temps, votre expertise, vos analyses et vos
            recommandations, que le deal aboutisse ou non. Utile pour structurer une relation de long terme avec un client
            récurrent. Nécessite un contrat de prestation de services signé, avec description précise des missions, tarif
            horaire ou forfaitaire, modalités de facturation. Ce mode rassure certains clients car il n'y a pas de
            pourcentage lié au montant de la transaction.
          </p>
        </Sub>

        <Sub title="Mode 3 : La Marge Commerciale (Trading Margin)">
          <p>
            Vous achetez le produit auprès du vendeur et le revendez à l'acheteur. Votre rémunération est la différence
            entre votre prix d'achat et votre prix de vente. Ce mode vous donne le plus grand contrôle sur le deal, mais
            nécessite : une structure juridique solide (vous êtes le vendeur légal), un compte bancaire business dédié,
            et souvent un fonds de roulement ou une ligne de financement car vous devez techniquement payer le vendeur
            avant (ou simultanément) d'être payé par l'acheteur.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Entités" title="Depuis quelle entité facturer ?" index="5.2">
        <Sub title="Auto-entrepreneur en France 🇫🇷">
          <p>
            Simple à créer, zéro gestion comptable complexe, idéal pour débuter. Mais les inconvénients sont significatifs :
            imposition sur le revenu selon le barème progressif (jusqu'à 45 % + prélèvements sociaux = taux marginal effectif
            jusqu'à ~62 %), plafond de chiffre d'affaires annuel de <Bold>77 700 € pour les prestations de services</Bold>
            (seuil 2024, révisé chaque année), pas de TVA récupérable. Clairement <Bold>pas adapté aux gros volumes de commission</Bold>.
          </p>
        </Sub>

        <Sub title="SASU ou EURL en France 🇫🇷">
          <p>
            Permet de séparer la rémunération personnelle (salaire) des bénéfices (dividendes), pour une optimisation
            fiscale significative. <Bold>Impôt sur les sociétés :</Bold> 15 % sur les 42 500 premiers euros de bénéfice
            annuel, puis 25 % au-delà. Les dividendes distribués sont taxés à la <Bold>flat tax de 30 %</Bold> (12,8 % d'IR
            + 17,2 % de prélèvements sociaux).
          </p>
          <Callout icon={Receipt} title="Exemple concret">
            <p>
              100 000 € de commission nette dans une SASU → IS 25 % = 25 000 € → bénéfice net : 75 000 € → dividende
              flat tax 30 % = 22 500 € → vous touchez réellement <Bold>52 500 €</Bold>. C'est bien sûr une simplification,
              mais cela illustre l'importance de la structure.
            </p>
          </Callout>
        </Sub>

        <Sub title="LLC Dubai / UAE 🇦🇪">
          <p>
            Depuis juin 2023, les Émirats ont introduit un impôt sur les sociétés de <Bold>9 % sur les bénéfices supérieurs
            à AED 375 000</Bold> (environ 100 000 USD). En dessous de ce seuil, les bénéfices sont exonérés. Les dividendes
            versés aux actionnaires non-résidents ne sont pas taxés aux UAE. Nécessite une <Bold>vraie présence économique</Bold>
            (licence, bureau, activité réelle) pour bénéficier pleinement du traitement fiscal.
          </p>
        </Sub>

        <Sub title="Free Zone Company UAE (DMCC, JAFZA, DAFZA, Dubai South…) 🇦🇪">
          <p>
            Certaines freezones maintiennent des avantages fiscaux spécifiques sous conditions. Le <Bold>DMCC (Dubai Multi
            Commodities Centre)</Bold> est particulièrement adapté aux traders de commodités : il offre un environnement
            réglementaire pensé pour ce secteur, avec un accès à des entrepôts, des chambres froides, des laboratoires
            d'analyse. Les frais de création varient de AED 15 000 à AED 50 000 selon la freezone et le type d'activité.
          </p>
        </Sub>

        <Sub title="Société à Maurice (Global Business License — GBL) 🇲🇺">
          <p>
            Sous le régime GBL, le taux d'impôt effectif sur les revenus de source étrangère peut descendre à <Bold>3 %</Bold>
            grâce à des crédits d'impôt partiels. Maurice a signé des conventions fiscales avec 46 pays (dont la France,
            l'Inde, et de nombreux pays africains), ce qui permet de rapatrier des revenus avec une fiscalité très réduite.
            Idéal pour structurer les flux commerciaux avec l'Afrique subsaharienne. Coût de création d'une GBL : environ
            3 000 $ à 5 000 $, plus frais annuels d'environ 2 000 $ à 3 000 $ pour un agent résident.
          </p>
        </Sub>

        <Sub title="Société au Luxembourg 🇱🇺">
          <p>
            Taux d'IS effectif : environ <Bold>17 %</Bold> (IS + impôt commercial, taux 2024). Accès au réseau de directives
            européennes (directive mère-fille, directive intérêts et redevances) permettant des transferts entre sociétés
            européennes avec retenues à la source réduites ou nulles. Idéal pour centraliser des flux intra-européens
            importants ou pour une holding qui détient des participations dans plusieurs pays européens.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Conventions" title="Double imposition & conventions fiscales" index="5.3">
        <Sub title="Comprendre le principe">
          <p>
            Un même revenu peut être imposé dans deux pays : le pays où il est généré (<Bold>pays de source</Bold>) et le
            pays où vous résidez (<Bold>pays de résidence fiscale</Bold>). Sans protection, vous payez deux fois des impôts
            sur le même argent. Les conventions fiscales bilatérales entre pays résolvent ce problème en définissant quel
            pays a le droit d'imposer chaque type de revenu, et dans quelle proportion.
          </p>
        </Sub>

        <Sub title="Conventions clés pour l'intermédiaire international">
          <ul className="space-y-3">
            <li><Bold>France → UAE :</Bold> il n'existe pas de convention fiscale bilatérale entre la France et les UAE
            au sens traditionnel, mais les UAE n'imposent pas les revenus des non-résidents. Résultat : les commissions
            reçues sur un compte UAE peuvent être rapatriées en France avec une imposition uniquement française (sous
            réserve de déclaration correcte).</li>
            <li><Bold>France → Maurice :</Bold> la convention franco-mauricienne prévoit une retenue à la source nulle
            sur les dividendes distribués à une société française détenant plus de 10 % de la société mauricienne, et
            une retenue de 5,5 % sur les intérêts. Les revenus de services peuvent être exonérés de retenue à la source
            sous conditions.</li>
            <li><Bold>France → Luxembourg :</Bold> la directive mère-fille européenne permet des dividendes intra-groupe
            sans retenue à la source entre filiales européennes sous conditions de détention.</li>
            <li><Bold>France → Singapour :</Bold> la convention franco-singapourienne prévoit des retenues à la source
            nulles ou réduites sur la plupart des revenus passifs.</li>
            <li><Bold>UAE → tout pays :</Bold> les UAE ne prélèvent généralement aucune retenue à la source sur les
            paiements sortants pour les non-résidents. C'est l'un des avantages majeurs de la juridiction pour les
            intermédiaires internationaux.</li>
          </ul>
        </Sub>
      </Section>

      <Section eyebrow="Arbitrage" title="Choisir sa structure selon son profil" index="5.4">
        <Sub title="Profil 1 : Débutant (commissions annuelles inférieures à 150 000 €)">
          <p>
            <Bold>Recommandation :</Bold> commencer en SASU en France. Frais de création : environ 1 500 à 2 500 € avec
            un avocat. Ouvrir un compte professionnel dédié (Qonto, Shine, ou une banque traditionnelle). Travailler avec
            un expert-comptable dès le premier euro de chiffre d'affaires (coût : 80 à 150 € par mois). Déclarer tous les
            revenus étrangers correctement (formulaires 2047 et 3916). Conserver scrupuleusement tous les contrats (NCNDA,
            IMFPA, factures de commission).
          </p>
        </Sub>

        <Sub title="Profil 2 : Confirmé (commissions annuelles de 150 000 à 2 millions d'euros)">
          <p>
            <Bold>Recommandation :</Bold> SASU France pour les activités locales + ouverture d'un compte bancaire
            professionnel UAE ou Singapour pour recevoir les commissions de source étrangère. Optimisation via la
            combinaison rémunération personnelle + dividendes dans la SASU. Consultation obligatoire avec un expert-comptable
            spécialisé en commerce international ou un avocat fiscaliste. À partir de ce niveau, une analyse annuelle de
            la structure fiscale est indispensable.
          </p>
        </Sub>

        <Sub title="Profil 3 : Avancé (commissions annuelles supérieures à 2 millions d'euros)">
          <p>
            <Bold>Recommandation :</Bold> l'optimisation fiscale à ce niveau nécessite une réflexion globale sur la
            résidence fiscale. Le changement de résidence fiscale vers un pays à faible imposition (UAE, Portugal sous le
            régime des Résidents Non Habituels — RNH, Malte, Monaco) combiné à une structure locale dans ce pays peut
            permettre de réduire considérablement la charge fiscale de manière totalement légale. Un avocat fiscaliste
            international est absolument indispensable. Le coût de cette optimisation (avocat, création de structures,
            compliance annuelle) est généralement rentabilisé à partir de 500 000 € de commissions annuelles.
          </p>
        </Sub>
      </Section>
    </div>
  );
}

// =============================================================
// EN
// =============================================================
function TaxationEN() {
  return (
    <div data-testid="view-taxation" className="max-w-5xl">
      <Hero
        moduleNum="05"
        eyebrowLabel="Module"
        icon={Calculator}
        italic="Tax"
        accent="& compensation"
        description="This is the module most brokers neglect — until the day they get a tax adjustment, or realise they let years of poorly optimised income slip by. Structuring your compensation properly is just as important as closing the deals themselves."
      />

      <Section eyebrow="Modes" title="How to get paid legally" index="5.1">
        <Sub title="Mode 1: The brokerage commission (success fee)">
          <p>
            The most common mode. The commission is payable ONLY if the transaction actually closes (payment received).
            It must be formally stipulated in the IMFPA with: the exact percentage, the calculation base (on which
            amount), the payment currency, the beneficiary bank account, and the payment timing (often simultaneous
            with the principal settlement, via the correspondent bank).
          </p>
          <p>
            Fiscally, this is professional activity income. It must be declared in the beneficiary's country of tax
            residence and potentially in the source country, depending on the applicable treaties.
          </p>
        </Sub>

        <Sub title="Mode 2: Consulting / advisory fees">
          <p>
            Independent of whether the deal closes. You're paid for your time, expertise, analyses and recommendations,
            whether the deal happens or not. Useful to structure a long-term relationship with a recurring client.
            Requires a signed services contract, with a precise description of the missions, hourly or flat rate, and
            invoicing terms. This mode reassures some clients because there's no percentage tied to the transaction value.
          </p>
        </Sub>

        <Sub title="Mode 3: Trading margin">
          <p>
            You buy the product from the seller and resell it to the buyer. Your fee is the difference between your buy
            and sell prices. This mode gives you the greatest deal control, but requires: a solid legal structure (you
            are the legal seller), a dedicated business bank account, and often working capital or a financing line —
            because technically you must pay the seller before (or at the same time as) being paid by the buyer.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Entities" title="Which entity should you invoice from?" index="5.2">
        <Sub title="French sole trader (auto-entrepreneur) 🇫🇷">
          <p>
            Easy to set up, zero complex bookkeeping, ideal for getting started. But the downsides are significant:
            personal income tax on the progressive scale (up to 45% + social charges = effective marginal rate up to
            ~62%), annual revenue cap of <Bold>€77,700 for services</Bold> (2024 threshold, revised yearly), no
            recoverable VAT. Clearly <Bold>not suitable for high commission volumes</Bold>.
          </p>
        </Sub>

        <Sub title="French SASU or EURL 🇫🇷">
          <p>
            Lets you separate personal compensation (salary) from profits (dividends), enabling significant tax
            optimisation. <Bold>Corporate income tax:</Bold> 15% on the first €42,500 of annual profit, then 25% beyond.
            Distributed dividends are taxed at the <Bold>30% flat tax</Bold> (12.8% income tax + 17.2% social charges).
          </p>
          <Callout icon={Receipt} title="Concrete example">
            <p>
              €100,000 of net commission in a SASU → 25% CIT = €25,000 → net profit: €75,000 → dividend at 30% flat
              tax = €22,500 → you actually pocket <Bold>€52,500</Bold>. It's a simplification, but it illustrates the
              importance of structure.
            </p>
          </Callout>
        </Sub>

        <Sub title="UAE LLC (Dubai) 🇦🇪">
          <p>
            Since June 2023, the UAE introduced a corporate tax of <Bold>9% on profits above AED 375,000</Bold> (about
            $100,000). Below that threshold, profits are exempt. Dividends to non-resident shareholders aren't taxed in
            the UAE. Requires <Bold>real economic substance</Bold> (license, office, real activity) to fully benefit
            from the tax treatment.
          </p>
        </Sub>

        <Sub title="UAE Free Zone Company (DMCC, JAFZA, DAFZA, Dubai South…) 🇦🇪">
          <p>
            Some freezones maintain specific tax advantages under conditions. The <Bold>DMCC (Dubai Multi Commodities
            Centre)</Bold> is particularly suited to commodity traders: it offers a regulatory environment built for
            this sector, with access to warehouses, cold rooms, and assay labs. Setup fees range from AED 15,000 to
            AED 50,000 depending on the freezone and activity type.
          </p>
        </Sub>

        <Sub title="Mauritius Company (Global Business License — GBL) 🇲🇺">
          <p>
            Under the GBL regime, the effective tax rate on foreign-source income can drop to <Bold>3%</Bold> through
            partial tax credits. Mauritius has signed tax treaties with 46 countries (including France, India and many
            African countries), allowing repatriation of income with very reduced taxation. Ideal to structure
            commercial flows with sub-Saharan Africa. GBL setup cost: around $3,000 to $5,000, plus annual fees of
            around $2,000 to $3,000 for a resident agent.
          </p>
        </Sub>

        <Sub title="Luxembourg company 🇱🇺">
          <p>
            Effective CIT rate: about <Bold>17%</Bold> (CIT + commercial tax, 2024). Access to the European directive
            network (parent-subsidiary directive, interests and royalties directive) allowing transfers between
            European companies with reduced or zero withholding tax. Ideal to centralise large intra-European flows or
            for a holding owning stakes in several European countries.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Treaties" title="Double taxation & tax treaties" index="5.3">
        <Sub title="Understanding the principle">
          <p>
            The same income can be taxed in two countries: the country where it's generated (<Bold>source country</Bold>)
            and the country where you reside (<Bold>tax residence country</Bold>). Without protection, you pay tax twice
            on the same money. Bilateral tax treaties between countries solve this by defining which country has the
            right to tax each type of income, and in what proportion.
          </p>
        </Sub>

        <Sub title="Key treaties for the international broker">
          <ul className="space-y-3">
            <li><Bold>France → UAE:</Bold> there is no traditional bilateral tax treaty between France and the UAE, but
            the UAE doesn't tax non-resident income. Result: commissions received on a UAE account can be repatriated
            to France with French taxation only (subject to proper declaration).</li>
            <li><Bold>France → Mauritius:</Bold> the France-Mauritius treaty provides zero withholding tax on dividends
            distributed to a French company holding more than 10% of the Mauritian company, and a 5.5% withholding on
            interest. Service income may be exempt from withholding under conditions.</li>
            <li><Bold>France → Luxembourg:</Bold> the EU parent-subsidiary directive allows intra-group dividends
            without withholding tax between European subsidiaries, under holding conditions.</li>
            <li><Bold>France → Singapore:</Bold> the France-Singapore treaty provides zero or reduced withholding on
            most passive income.</li>
            <li><Bold>UAE → any country:</Bold> the UAE generally doesn't levy any withholding tax on outbound payments
            to non-residents. This is one of the major advantages of the jurisdiction for international brokers.</li>
          </ul>
        </Sub>
      </Section>

      <Section eyebrow="Arbitrage" title="Choosing your structure based on your profile" index="5.4">
        <Sub title="Profile 1: Beginner (annual commissions below €150,000)">
          <p>
            <Bold>Recommendation:</Bold> start with a SASU in France. Setup cost: about €1,500 to €2,500 with a lawyer.
            Open a dedicated business account (Qonto, Shine, or a traditional bank). Work with an accountant from the
            very first euro of revenue (cost: €80 to €150 per month). Declare all foreign income properly (forms 2047
            and 3916). Keep all contracts (NCNDA, IMFPA, commission invoices) carefully archived.
          </p>
        </Sub>

        <Sub title="Profile 2: Confirmed (annual commissions €150,000 to €2 million)">
          <p>
            <Bold>Recommendation:</Bold> SASU France for local activity + opening of a UAE or Singapore business bank
            account to receive foreign-source commissions. Optimisation through the personal salary + dividends mix in
            the SASU. Mandatory consultation with an accountant specialised in international trade or a tax lawyer. At
            this level, an annual review of the tax structure is essential.
          </p>
        </Sub>

        <Sub title="Profile 3: Advanced (annual commissions above €2 million)">
          <p>
            <Bold>Recommendation:</Bold> tax optimisation at this level requires a global rethink of tax residence.
            Changing tax residence to a low-tax country (UAE, Portugal under the NHR — Non-Habitual Resident regime,
            Malta, Monaco) combined with a local structure in that country can dramatically reduce the tax burden,
            entirely legally. An international tax lawyer is absolutely indispensable. The cost of this optimisation
            (lawyer, structures setup, annual compliance) is typically recouped from €500,000 of annual commissions.
          </p>
        </Sub>
      </Section>
    </div>
  );
}
